// "Original" in the review player's quality menu (Ryan, 2026-10-25: "Yes, for everyone"): the editor's own uploaded file
// at full quality, played by the browser straight from storage. Stream's copy tops out at 1080p and about 4 Mbps; a 4K
// master is often ten times that. The server says whether there is an Original and what it is
// (GET /projects/{id}/original/{version}; backend/app/original_play.py); this file says whether THIS browser is offered
// it, what the menu calls it, the note under it, and when its signed link is asked for again. Pure; node-tested
// (originalQuality.test.mjs).

export const FAMILIES = ['h264', 'hevc'];

// A signed link is asked for again this long before it ends (or a quarter of its life, when that is shorter).
export const REFRESH_BEFORE_S = 120;

// Playing Original with the picture held and nothing ahead to play for this long counts as a failed link (Safari
// waits on a link that ended instead of calling it an error): asked for again once, then back to Auto.
export const STALL_S = 10;

/**
 * Is Original offered to this browser? The server has a stored file whose type a browser may play (`available`), and
 * this browser says it plays that exact codec (`canPlayType('<mime>; codecs="<codec>"')`, which answers "probably",
 * "maybe" or ""). H.264 (the server offers only 8-bit 4:2:0, which every browser plays): any yes. HEVC: only "probably".
 */
export function offered(info, canPlayType) {
  if (!info || info.available !== true || typeof info.url !== 'string' || !info.url) return false;
  if (typeof info.codec !== 'string' || !info.codec || !FAMILIES.includes(info.family)) return false;
  if (typeof canPlayType !== 'function') return false;
  let answer = '';
  try { answer = canPlayType(`${info.mime || 'video/mp4'}; codecs="${info.codec}"`) || ''; } catch { answer = ''; }
  if (info.family === 'h264') return answer === 'probably' || answer === 'maybe';
  return answer === 'probably';
}

/** "Original (2160p)"; "Original" when the height is not known. */
export function originalLabel(info) {
  const h = Math.round(Number(info && info.height));
  return Number.isFinite(h) && h > 0 ? `Original (${h}p)` : 'Original';
}

/** The file's own bitrate in Mbps for the note: whole numbers from 10 up, one decimal below; null when not known. */
export function mbps(bitrate) {
  const n = Number(bitrate);
  if (!Number.isFinite(n) || n <= 0) return null;
  const m = n / 1e6;
  if (m >= 10) return String(Math.round(m));
  const one = Math.max(0.1, Math.round(m * 10) / 10);
  return one.toFixed(1).replace(/\.0$/, '');
}

/** The note under the choice. */
export function speedNote(info) {
  const m = mbps(info && info.bitrate);
  return m ? `Full quality. Needs a fast connection (about ${m} Mbps).` : 'Full quality. Needs a fast connection.';
}

/** Milliseconds from `now` until the link (got at `gotAt`, alive `expiresIn` seconds) is asked for again; 0 when due. */
export function refreshInMs(expiresIn, gotAt, now) {
  const life = Number(expiresIn) > 0 ? Number(expiresIn) : 900;
  const lead = Math.min(REFRESH_BEFORE_S, life / 4);
  const since = Number.isFinite(gotAt) && Number.isFinite(now) ? Math.max(0, now - gotAt) : Infinity;
  return Math.max(0, Math.round((life - lead) * 1000 - since));
}

/**
 * Where a switched source starts: the spot the viewer was at, and the Stream rung to put the player on when it goes
 * back to Stream's copy (-1 = Auto; Original is -1 too).
 */
export function resumeFrom(time, level) {
  const t = Number(time);
  return { t: Number.isFinite(t) && t > 0 ? t : 0, level: Number.isInteger(level) && level >= 0 ? level : -1 };
}

/** Is the new source far enough from the spot to be moved there? (More than a frame at 25 fps.) */
export function needsSeek(current, target) {
  const c = Number(current);
  const t = Number(target);
  if (!Number.isFinite(t) || t <= 0) return false;
  return !Number.isFinite(c) || Math.abs(c - t) > 0.04;
}
